import { type ClassValue, clsx } from 'clsx'
import { twMerge } from 'tailwind-merge'

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

import { CalendarDate } from '@internationalized/date';
import { jwtDecode } from "jwt-decode";

export const normalizePhone = (value: any) => {
  let digits = String(value ?? '').replace(/\D/g, '');
  if (digits.length === 11 && digits.startsWith('1')) digits = digits.slice(1);
  return digits.slice(0, 10);
};

export const formatPhoneInput = (value: any) => {
  const digits = normalizePhone(value);
  if (digits.length < 4) return digits ? `(${digits}` : '';
  if (digits.length < 7) return `(${digits.slice(0, 3)}) ${digits.slice(3)}`;
  return `(${digits.slice(0, 3)}) ${digits.slice(3, 6)}-${digits.slice(6)}`;
};

export const formatPhoneNumber = (phoneNumber: any) => {
  const cleaned = phoneNumber?.replace(/\D/g, '');

  if (!cleaned || cleaned.length !== 10) {
    return phoneNumber;
  }

  return `(${cleaned.slice(0, 3)}) ${cleaned.slice(3, 6)}-${cleaned.slice(6, 10)}`;
};

export const monthDayYear = (dateString: any) => {
  if (!dateString) return '';
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
  }).format(date)
};

export const hoursMinutes = (dateString: any) => {
  if (!dateString) return '';
  const date = new Date(dateString)
  return new Intl.DateTimeFormat('en-US', {
    hour: 'numeric',
    minute: 'numeric',
    hour12: true,
  }).format(date)
};

export const formatTime = (time24: any) => {
  if (!time24) return '';

  const [hours24, minutes] = time24.split(':');
  let hours12 = parseInt(hours24, 10) % 12;

  hours12 = hours12 === 0 ? 12 : hours12;

  const period = parseInt(hours24, 10) >= 12 ? 'PM' : 'AM';

  return `${hours12}:${minutes} ${period}`;
}

export const exportTableToCSV = (filename: any, headers: any) => {
  const csv = [];

  csv.push(headers.join(','));

  const rows = document.querySelectorAll("table tbody tr");

  for (const row of rows) {
      const cols = row.querySelectorAll("td");
      const rowData = [];

      for (const col of cols) {
          const value = col.textContent.trim();
          rowData.push(value.includes(',') || value.includes('\n') || value.includes('"')
              ? `"${value.replace(/"/g, '""')}"`
              : value
          );
      }

      csv.push(rowData.join(","));
  }

  const csvString = csv.join("\n");
  const blob = new Blob([csvString], { type: "text/csv;charset=utf-8" });
  const link = document.createElement("a");
  link.href = URL.createObjectURL(blob);
  link.download = filename || "export.csv";
  link.click();
};

export const address = (address: any) => {
  if (!address) return '';
  const { address1, address2, city, state, zipCode } = address;
  const { address_1, address_2, zip_code } = address;

  return `
    ${address1 || address_1},
    ${(address2 || address_2) ? (address2 || address_2)+ ',' : ''}
    ${city},
    ${state}
    ${zipCode || zip_code}`
};

const formatStringForMap = (str: any) => str.split(" ").join("+");
const googleMapsBaseUrl = "https://www.google.com/maps/place/";

export const addressToMapLink = (address: any) => {
  if (!address) return '#';
  const { address1, address_1, address2, address_2, city, state, zipCode, zip_code } = address;
  const addressIfExists = () => (address2 || address_2) ? `${address2 || address_2}+` : ''
  const cityStateZip = `${city}+${state}+${zipCode || zip_code}`
  return `${googleMapsBaseUrl}${formatStringForMap((address1 || address_1))}+${addressIfExists()}${cityStateZip}`;
}

export const breakpoints = {
  sm: 640,
  md: 768,
  lg: 1024,
  xl: 1280,
  "2xl": 1536
};

export const createDateObject = (date: any) => {
  const [month, day, year] = date.split("/").map(Number);

  const calendarDate = new CalendarDate(year, month, day);
  return calendarDate;
}

export const checkBreakpoint = (innerWidth: any) => innerWidth < breakpoints.md;

// @ts-ignore
export const isValidToken = (token: any) => {
  try {
    const decoded = jwtDecode(token);
    return decoded && decoded.exp && decoded.exp > Date.now() / 1000;
  } catch (e) {
    return false;
  }
}

export const months = [
  'January', 'February', 'March', 'April', 'May', 'June',
  'July', 'August', 'September', 'October', 'November', 'December'
];

export const weekdays = ['SUNDAY', 'MONDAY', 'TUESDAY', 'WEDNESDAY', 'THURSDAY', 'FRIDAY', 'SATURDAY'];

const TEAM_ROLES = ['OWNER', 'MANAGER', 'MEMBER'];
const roleList = (roles: any): string[] => (Array.isArray(roles) ? roles : []);
export const hasRole = (roles: any, role: string) => roleList(roles).includes(role);
export const hasTeamRole = (roles: any) => TEAM_ROLES.some((role) => hasRole(roles, role));
export const canManageTeam = (roles: any) => hasRole(roles, 'OWNER') || hasRole(roles, 'MANAGER');
export const teamLabel = (roles: any) => (hasRole(roles, 'OWNER') ? 'My lots' : 'Team');
export const roleLabel = (roles: any) => TEAM_ROLES.find((role) => hasRole(roles, role)) || 'PARKER';
export const isOnsiteEmail = (email: any) => typeof email === 'string' && email.toLowerCase().endsWith('@onsite.spotsurfer.com');

export const LOT_TIME_ZONE = 'America/Denver';
export const lotToday = () => new Date().toLocaleDateString('en-US', { timeZone: LOT_TIME_ZONE, month: '2-digit', day: '2-digit', year: 'numeric' });
export const lotClock = (date) => date.toLocaleTimeString('en-US', { timeZone: LOT_TIME_ZONE, hour: 'numeric', minute: '2-digit' });
